import { motion } from "framer-motion";
import { getCountryName } from "utils/getCountryName";

export type ChartSegment = {
  label: string;
  value: number;
  color?: string;
};

type SegmentedBarChartProps = {
  data: ChartSegment[];
};

const COLORS = [
  "bg-blue-900",
  "bg-blue-700",
  "bg-blue-500",
  "bg-blue-300",
  "bg-blue-200",
];

const SegmentedBarChart = ({ data }: SegmentedBarChartProps) => {
  if (!data || data.length === 0) {
    return (
      <div className="p-6 text-center text-slate-500">
        Dados insuficientes para exibição.
      </div>
    );
  }

  // Ordena do maior para o menor
  const sorted = [...data].sort((a, b) => b.value - a.value);

  // Agrupamento (>4 vira top 3 + outros)
  let normalized: ChartSegment[];

  if (sorted.length <= 4) {
    normalized = sorted;
  } else {
    const top3 = sorted.slice(0, 3);
    const othersValue = sorted
      .slice(3)
      .reduce((acc, item) => acc + item.value, 0);

    normalized = [
      ...top3,
      { label: "Outros", value: othersValue, color: "bg-blue-300" },
    ];
  }

  const total = normalized.reduce((acc, item) => acc + item.value, 0);

  const segments = normalized.map((item, index) => ({
    ...item,
    percentage: (item.value / total) * 100,
    color: item.color ?? COLORS[index % COLORS.length],
  }));

  return (
    <>
      {/* Wrapper que permite exibir os percentuais sem quebrar o border-radius */}
      <div className="relative mb-10">
        {/* Barras */}
        <div className="flex flex-col h-20 relative">
          <div className="relative z-10 flex gap-2 h-14 rounded-2xl overflow-hidden shadow-inner bg-slate-200/50">
            {segments.map((segment, index) => (
              <div
                key={segment.label}
                className="relative h-full "
                style={{ width: `${segment.percentage}%` }}
              >
                <motion.div
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{
                    duration: 0.8,
                    delay: 0.3 + index * 0.12,
                    ease: "easeOut",
                  }}
                  style={{ transformOrigin: "left" }}
                  whileHover={{ scaleY: 1.05 }}
                  className={`${segment.color} relative h-full w-full`}
                >
                  {/* Shimmer */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
                    animate={{ x: ["-100%", "200%"] }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatDelay: 3,
                      ease: "easeInOut",
                    }}
                    style={{ transform: "skewX(-20deg)" }}
                  />
                </motion.div>
              </div>
            ))}
          </div>
          <div className="w-full flex">
            {segments.map((segment, index) => (
              <>
                {segment.percentage >= 6 && (
                  <span
                    className="mt-1 text-center text-xs text-slate-600 whitespace-nowrap"
                    style={{ width: `${segment.percentage}%` }}
                    key={index}
                  >
                    {segment.percentage.toFixed(0)}%
                  </span>
                )}
              </>
            ))}
          </div>
        </div>
        {/* Lista */}

        <div className="relative z-10 space-y-3">
          {segments.map((segment) => (
            <motion.div
              key={segment.label}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              whileHover={{ x: 5, scale: 1.02 }}
              className="flex items-center justify-between p-3 rounded-xl bg-white/50 hover:bg-white/80 transition-all cursor-pointer group"
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-4 h-4 rounded-full ${segment.color} shadow-lg`}
                />
                <span className="text-shopify-body text-shopify-medium">
                  {getCountryName(segment.label)}
                </span>
              </div>

              <span className="text-shopify-body text-shopify-light">
                USD${" "}
                {segment.value.toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                })}
              </span>
            </motion.div>
          ))}

          {/* Total */}
          <div className="flex items-center justify-between pt-4 px-3 border-t-2 border-slate-300/50">
            <span className="font-semibold">Total</span>

            <span className="font-semibold">
              USD${" "}
              {total.toLocaleString("pt-BR", {
                minimumFractionDigits: 2,
              })}
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default SegmentedBarChart;
